import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import ClassPassList from './ClassPassList/ClassPassList';
import ModalRegisterPass from './ModalRegisterPass/ModalRegisterPass';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMembershipTemplatesListAPI, reorderMembershipTemplatesAPI } from '../../api/memberShip';
import _ from 'lodash';
import FloatingButton from './FloatingButton';
import ClassPassDetail from './ClassPassDetail/ClassPassDetail';
import ModalModifyPass from './ModalModifyPass/ModalModifyPass';
import ModalReorderPass from './ModalReorderPass/ModalReorderPass';
import {
    CUSTOM_SORT_TYPE,
    DEFAULT_FIRST_SORT_TYPE,
    DEFAULT_SECOND_SORT_TYPE,
    FIRST_SORT_OPTIONS,
    SECOND_SORT_OPTIONS,
    readStoredSortType,
} from './sortOptions';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import GroupTicketCard from './GroupTicketCard';
import GroupTicketDetail from './GroupTicketDetail';
import ModalRegisterGroupTicket from './ModalRegisterGroupTicket';
import { useTickets } from '@/features/groupTicket/hooks';
import { Ticket } from '@/features/groupTicket/types';
import GroupClassManagePage from '../GroupClass/GroupClassManagePage';
import GroupSettingsPage from '../GroupSettings/GroupSettingsPage';
import { colors } from '../../lib/colors';
import { Body2Regular } from '../../lib/font';

export interface SortType {
    value: string;
    label: string;
}

export type ReorderNotice = { tone: 'success' | 'error'; message: string };

const GROUP_SUB_TABS = [
    { key: 'tickets' as const, label: '그룹수업 수강권' },
    { key: 'classes' as const, label: '그룹수업 관리' },
    { key: 'settings' as const, label: '그룹수업 예약 설정' },
];

const ClassPassPage = ({ authObject, setAuthObject, authorization, createAlert }: any) => {
    const location = useLocation();

    // 마운트 시점 location.state 또는 쿼리에서 초기 탭 결정 (lazy init — 둘 다 안 오면 'pt')
    const [pageType, setPageType] = useState<string>('list');
    const [ticketTab, setTicketTab] = useState<'pt' | 'group'>(() => {
        const fromState = (location.state as any)?.initialTab;
        if (fromState === 'group' || fromState === 'pt') return fromState;
        const fromQuery = new URLSearchParams(window.location.search).get('tab');
        return fromQuery === 'group' ? 'group' : 'pt';
    });
    const [groupSubTab, setGroupSubTab] = useState<'tickets' | 'classes' | 'settings'>(() => {
        const fromState = (location.state as any)?.initialSub;
        if (fromState === 'classes' || fromState === 'settings' || fromState === 'tickets') return fromState;
        const fromQuery = new URLSearchParams(window.location.search).get('sub');
        return fromQuery === 'classes' || fromQuery === 'settings' || fromQuery === 'tickets' ? fromQuery : 'tickets';
    });
    const [selectedGroupTicket, setSelectedGroupTicket] = useState<Ticket | null>(null); // ★ 선택된 수업 수강권
    const [showGroupGuide, setShowGroupGuide] = useState(() => localStorage.getItem('guide_groupClassPass') !== 'hidden');

    const [isRegisterPassModal, setIsRegisterPassModal] = useState<boolean>(false); // 수강권 등록 모달여부
    const [isModifyPassModal, setIsModifyPassModal] = useState<boolean>(false); // 수강권 수정 모달여부
    const [isReorderPassModal, setIsReorderPassModal] = useState<boolean>(false); // 수강권 표시 순서 모달여부
    const reorderInFlightRef = useRef(false); // 빠른 연속 클릭으로 같은 요청이 두 번 나가는 것을 막는 동기 잠금
    // 순서 저장 결과는 공통 알럿(모달) 대신 목록 위 인라인 배너로 알린다. 모달 위에 모달을 겹치면
    // 키보드 포커스가 알럿에 닿지 않고, 공통 알럿은 포커스 관리가 없어서다. 성공은 잠시 뒤 사라진다.
    const [reorderNotice, setReorderNotice] = useState<ReorderNotice | null>(null);
    const reorderNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showReorderNotice = (notice: ReorderNotice) => {
        if (reorderNoticeTimerRef.current) clearTimeout(reorderNoticeTimerRef.current);
        setReorderNotice(notice);
        if (notice.tone === 'success') {
            reorderNoticeTimerRef.current = setTimeout(() => setReorderNotice(null), 5000);
        }
    };
    useEffect(() => () => {
        if (reorderNoticeTimerRef.current) clearTimeout(reorderNoticeTimerRef.current);
    }, []);
    const queryClient = useQueryClient();
    const [isGroupTicketModal, setIsGroupTicketModal] = useState(false); // ★ 수업 수강권 등록 모달

    const centerId = authObject?.centerId;
    const { data: groupTickets = [] } = useTickets({ centerId, params: { isActive: 'false' }, enabled: !!centerId });
    const [ticketStatusFilter, setTicketStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');

    // 필터 + 정렬 (활성 우선, 비활성은 뒤로)
    const displayGroupTickets = useMemo(() => {
        const list = [...(groupTickets as Ticket[])];
        list.sort((a, b) => {
            if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
            return (a.createdAt || '').localeCompare(b.createdAt || '');
        });
        if (ticketStatusFilter === 'active') return list.filter((t) => t.isActive);
        if (ticketStatusFilter === 'inactive') return list.filter((t) => !t.isActive);
        return list;
    }, [groupTickets, ticketStatusFilter]);

    const activeCount = (groupTickets as Ticket[]).filter((t) => t.isActive).length;
    const inactiveCount = (groupTickets as Ticket[]).filter((t) => !t.isActive).length;

    const storedFirstSortType = localStorage.getItem('firstSortType');
    const storedSecondSortType = localStorage.getItem('secondSortType');
    const storedIsHideCoach = localStorage.getItem('isHideCoach');

    // 손상된 값·예전 초기화 버그가 남긴 false·사라진 옵션은 기본값으로 돌린다 (sortOptions.readStoredSortType)
    const [firstSortType, setFirstSortType] = useState<SortType>(() =>
        readStoredSortType(storedFirstSortType, FIRST_SORT_OPTIONS, DEFAULT_FIRST_SORT_TYPE)
    );

    const [secondSortType, setSecondSortType] = useState<SortType>(() =>
        readStoredSortType(storedSecondSortType, SECOND_SORT_OPTIONS, DEFAULT_SECOND_SORT_TYPE)
    );

    const [membershipTemplates, setMembershipTemplates] = useState<any>([]); // 수강권 리스트
    const [filterMembershipTemplates, setFilterMembershipTemplates] = useState<any>([]); // 수강권 필터 리스트

    const [detailObject, setDetailObject] = useState<any>(null); // 수강권 상세
    // 강사 숨기기 상태
    const [isHideCoach, setIsHideCoach] = useState<boolean>(() => {
        // 손상된 값이면 false. JSON.parse 가 던지면 페이지가 렌더 전에 죽는다.
        try {
            return storedIsHideCoach ? JSON.parse(storedIsHideCoach) === true : false;
        } catch (_error) {
            return false;
        }
    });

    // (GET) : 수강권 상품 조회
    const getMembershipTemplatesList = useQuery(
        ['getMembershipTemplatesListAPI'],
        async () => await getMembershipTemplatesListAPI(authorization, authObject?.centerId),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const response = res.data;

                    setMembershipTemplates(response?.membershipTemplates);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    // 수강권 수정하기 클릭 이벤트
    const onClickModifyPass = () => {
        setIsModifyPassModal(true);
    };

    // (PATCH) : 수강권 표시 순서 저장. 목록에 있는 상품 전체(활성·비활성·강사 생성)를 그대로 보낸다.
    // 결과가 무엇이든 모달은 닫고 결과는 인라인 배너로 알린다. 닫히면 포커스는 「순서 변경」 버튼으로 돌아간다.
    // 실패 시 관리자는 「순서 변경」을 다시 열어 재시도한다.
    const reorderMutation = useMutation(
        async (membershipTemplateIds: string[]) => reorderMembershipTemplatesAPI(authorization, membershipTemplateIds),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    // 저장한 순서를 바로 보여주도록 정렬을 「표시 순서」로 되돌린다.
                    setSecondSortType(CUSTOM_SORT_TYPE);
                    localStorage.setItem('secondSortType', JSON.stringify(CUSTOM_SORT_TYPE));
                    queryClient.invalidateQueries({ queryKey: ['getMembershipTemplatesListAPI'] });
                    queryClient.invalidateQueries({ queryKey: ['getMembershipTemplatesListAPIQuery'] });
                    showReorderNotice({ tone: 'success', message: '수강권 표시 순서를 저장했어요.' });
                    return;
                }
                if (res.status === 404) {
                    // 모달을 열어둔 사이 목록이 바뀐 경우. 모달은 열 때 스냅샷을 잡으므로 같은 모달에서
                    // 다시 저장해도 같은 id 배열이 나간다 → 새 목록으로 다시 열게 한다.
                    queryClient.invalidateQueries({ queryKey: ['getMembershipTemplatesListAPI'] });
                    showReorderNotice({
                        tone: 'error',
                        message:
                            '그 사이 수강권 목록이 바뀌어 저장하지 못했어요. 목록을 다시 불러왔으니 「순서 변경」을 다시 열어주세요.',
                    });
                    return;
                }
                if (res.status === 406 || res.status === 403) {
                    showReorderNotice({ tone: 'error', message: '수강권 표시 순서는 센터 관리자만 바꿀 수 있어요.' });
                    return;
                }
                showReorderNotice({
                    tone: 'error',
                    message: '순서를 저장하지 못했어요. 잠시 후 다시 시도해주세요. 오류가 지속된다면 라포채널로 문의바랍니다.',
                });
            },
            onError: () => {
                showReorderNotice({
                    tone: 'error',
                    message: '순서를 저장하지 못했어요. 잠시 후 다시 시도해주세요. 오류가 지속된다면 라포채널로 문의바랍니다.',
                });
            },
            onSettled: () => {
                reorderInFlightRef.current = false;
                setIsReorderPassModal(false);
            },
        }
    );

    const onApplyReorder = (membershipTemplateIds: string[]) => {
        if (reorderInFlightRef.current) return;
        reorderInFlightRef.current = true;
        setReorderNotice(null);
        reorderMutation.mutate(membershipTemplateIds);
    };
    // 수강권상세
    const onClickDetailPage = (item: any) => {
        setDetailObject(item);
        setPageType('detail');
    };
    // 수강권상세
    const onClickListPage = () => {
        setDetailObject(undefined);
        setPageType('list');
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (pageType) {
            scrollToTop();
        }
    }, [pageType]);

    // 상단 필터2개
    useEffect(() => {
        if (filterMembershipTemplates) {
            let resultList = _.cloneDeep(membershipTemplates);

            // 첫번째 활성 / 비활성 필터
            if (firstSortType?.value === 'ACTIVE') {
                resultList = resultList.filter((item: any) => item?.isAvailable === true);
            } else if (firstSortType?.value === 'INACTIVE') {
                resultList = resultList.filter((item: any) => item?.isAvailable === false);
            }

            // 두번째 표시 순서(서버 순서 그대로), 최근 생성, 최근 사용, 오래된 생성 정렬
            if (secondSortType?.value === 'CUSTOM') {
                // 서버가 sortOrder → createdAt desc 로 정렬해 내려준다. 손대지 않는다.
            } else if (secondSortType?.value === 'RECENT') {
                resultList.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            } else if (secondSortType?.value === 'ACTIVE') {
                resultList.sort(
                    (a: any, b: any) => new Date(b.recentUsedTime).getTime() - new Date(a.recentUsedTime).getTime()
                );
            } else if (secondSortType?.value === 'OLD') {
                resultList.sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            } else if (secondSortType?.value === 'ALPHABET') {
                resultList.sort((a: any, b: any) => a.name.localeCompare(b.name));
            }
            if (isHideCoach) {
                // 강사 숨기기
                resultList = resultList.filter((item: any) => item?.issuer.issuerRole !== 'coach');
            }

            setFilterMembershipTemplates(resultList);
        }
    }, [firstSortType, secondSortType, isHideCoach, membershipTemplates]);

    return (
        <Col
            style={{
                marginTop: 64,
                width: '100%',
                alignItems: 'center',
                position: 'relative',
                marginBottom: 80,
            }}
        >
            {/* ★ PT / 수업 수강권 탭 */}
            {pageType === 'list' && (
                <Row style={{ width: 1192, marginTop: 32, gap: 4 }}>
                    {([
                        { key: 'pt' as const, label: '1:1 PT 수강권 (기존)' },
                        { key: 'group' as const, label: '그룹수업 수강권' },
                    ]).map((tab) => {
                        const isActive = ticketTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => { setTicketTab(tab.key); setSelectedGroupTicket(null); setPageType('list'); setGroupSubTab('tickets'); }}
                                style={{
                                    padding: '10px 22px',
                                    fontSize: 14,
                                    fontWeight: isActive ? 600 : 400,
                                    borderRadius: 8,
                                    border: isActive ? '1.5px solid #2563EB' : '1px solid #E1E4E6',
                                    backgroundColor: isActive ? '#EFF6FF' : '#FFFFFF',
                                    color: isActive ? '#2563EB' : '#6B7280',
                                    cursor: 'pointer',
                                }}
                            >
                                {tab.label}
                                {tab.key === 'group' && groupTickets.length > 0 && (
                                    <span style={{
                                        marginLeft: 6,
                                        padding: '1px 7px',
                                        borderRadius: 10,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        backgroundColor: isActive ? '#2563EB' : '#E5E7EB',
                                        color: isActive ? 'white' : '#6B7280',
                                    }}>
                                        {groupTickets.length}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </Row>
            )}

            {/* ★ 그룹수업 서브탭 */}
            {pageType === 'list' && ticketTab === 'group' && (
                <Row
                    style={{
                        width: 1192,
                        marginTop: 20,
                        borderBottom: `1px solid ${colors.WHITE_600}`,
                        gap: 0,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Row style={{ gap: 0 }}>
                        {GROUP_SUB_TABS.map((tab) => (
                            <div
                                key={tab.key}
                                onClick={() => { setGroupSubTab(tab.key); setSelectedGroupTicket(null); }}
                                style={{
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    borderBottom: groupSubTab === tab.key ? `2px solid ${colors.PRIMARY_BLUE}` : '2px solid transparent',
                                    marginBottom: -1,
                                }}
                            >
                                <Body2Regular
                                    style={{
                                        color: groupSubTab === tab.key ? colors.PRIMARY_BLUE : colors.LAVEL_1,
                                        fontWeight: groupSubTab === tab.key ? 700 : 400,
                                    }}
                                >
                                    {tab.label}
                                </Body2Regular>
                            </div>
                        ))}
                    </Row>
                    {!showGroupGuide && (
                        <button
                            onClick={() => { setShowGroupGuide(true); localStorage.removeItem('guide_groupClassPass'); }}
                            style={{
                                marginRight: 4, width: 24, height: 24, borderRadius: '50%',
                                border: '1.5px solid #93C5FD', backgroundColor: '#EFF6FF',
                                color: '#3B82F6', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            title="안내 보기"
                        >?</button>
                    )}
                </Row>
            )}

            {/* ★ 그룹수업 안내 배너 */}
            {pageType === 'list' && ticketTab === 'group' && showGroupGuide && (
                <Col
                    style={{
                        width: 1192, marginTop: 16, padding: '20px 24px',
                        backgroundColor: '#EFF6FF', borderRadius: 12, border: '1px solid #BFDBFE',
                        position: 'relative', gap: 10,
                    }}
                >
                    <button
                        onClick={() => { setShowGroupGuide(false); localStorage.setItem('guide_groupClassPass', 'hidden'); }}
                        style={{
                            position: 'absolute', top: 12, right: 16,
                            background: 'none', border: 'none', fontSize: 16,
                            color: '#93C5FD', cursor: 'pointer',
                        }}
                    >✕</button>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1E40AF' }}>
                        📚 그룹수업 수강권 · 수업 · 예약 설정
                    </span>
                    <span style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.6 }}>
                        <b>그룹수업(GX · 1:2 · 1:3)</b>을 운영하려면 세 가지를 순서대로 설정하세요.
                        회원이 <b>앱에서 그룹수업을 직접 예약</b>할 수 있어요.
                    </span>
                    <Row style={{ gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                        <GuideStep num="1" title="수강권 등록"
                            desc="판매할 수강권 상품 (예: GX 10회권, 6:1 그룹수업)" />
                        <GuideArrow />
                        <GuideStep num="2" title="수업 관리"
                            desc="수업 템플릿 생성 및 허용 수강권 연결" />
                        <GuideArrow />
                        <GuideStep num="3" title="예약 설정"
                            desc="오픈 · 마감 · 취소 정책 설정" />
                    </Row>
                    <span style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                        ℹ 1:1 PT 수강권은 <b>'1:1 PT 수강권 (기존)'</b> 탭에서 별도 관리됩니다.
                    </span>

                    {/* ─── 1:1 PT → 2:1·3:1 그룹수업 전환 시나리오 ─── */}
                    <Col
                        style={{
                            marginTop: 10,
                            padding: '16px 18px',
                            backgroundColor: '#FFFFFF',
                            border: '1px dashed #93C5FD',
                            borderRadius: 10,
                            gap: 10,
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 16 }}>🔀</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1E3A8A' }}>
                                기존에 <b>2:1 · 3:1 수업을 1:1 PT로 처리</b>해오셨다면?
                            </span>
                        </Row>
                        <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                            지금까지 2명·3명이 함께 수업하는 경우를 어쩔 수 없이 1:1 PT 수강권을 각자 발급해서 운영하셨다면,
                            이제 <b>그룹수업 수강권</b>으로 깔끔하게 바꿀 수 있어요.
                            <br />
                            회원이나 기존 수강권을 <b>삭제하거나 환불할 필요는 없습니다.</b> 자연스럽게 전환하는 방법은 다음과 같아요 👇
                        </span>
                        <Col style={{ gap: 8, marginTop: 4 }}>
                            {[
                                {
                                    num: '1',
                                    title: '기존 1:1 PT 수강권은 그대로 두기',
                                    desc: '환불하거나 삭제하지 마세요. 회원이 남은 횟수를 다 쓸 때까지 1:1 PT 수강권으로 계속 수업하시면 됩니다.',
                                },
                                {
                                    num: '2',
                                    title: '새 그룹수업 수강권 상품 등록',
                                    desc: '이 페이지의 "+ 수강권 등록"으로 "듀얼PT 10회권(2:1)", "트리플PT 10회권(3:1)" 같은 상품을 만들어주세요.',
                                },
                                {
                                    num: '3',
                                    title: '기존 회원에게 새 수강권 재발급',
                                    desc: '회원 프로필의 "그룹수업" 탭에서 새로 만든 2:1 / 3:1 수강권을 각 회원에게 발급해주세요. (회원별로 발급 필요)',
                                },
                                {
                                    num: '4',
                                    title: '수업 관리에서 템플릿 만들고 수강권 연결',
                                    desc: '"수업 관리" 서브탭에서 "듀얼PT(2:1)" 수업 템플릿을 만들고, 방금 만든 2:1 수강권을 "허용 수강권"으로 지정합니다.',
                                },
                                {
                                    num: '5',
                                    title: '수업 생성 / 예약 — 새 수강권에서 차감',
                                    desc: '이후 진행하는 2:1 수업은 스케줄에 등록하면 각 회원의 그룹 수강권에서 1회씩 차감돼요. 기존 1:1 PT 수강권은 건드리지 않습니다.',
                                },
                            ].map((step) => (
                                <Row key={step.num} style={{ gap: 10, alignItems: 'flex-start' }}>
                                    <span
                                        style={{
                                            flexShrink: 0,
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            backgroundColor: '#3B82F6',
                                            color: 'white',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 1,
                                        }}
                                    >
                                        {step.num}
                                    </span>
                                    <Col style={{ flex: 1, gap: 2 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                                            {step.title}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.6 }}>
                                            {step.desc}
                                        </span>
                                    </Col>
                                </Row>
                            ))}
                        </Col>
                        <Col
                            style={{
                                marginTop: 6,
                                padding: '10px 12px',
                                backgroundColor: '#FEF3C7',
                                border: '1px solid #FDE68A',
                                borderRadius: 8,
                                gap: 4,
                            }}
                        >
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400E' }}>
                                💡 자주 헷갈리는 점
                            </span>
                            <span style={{ fontSize: 12, color: '#78350F', lineHeight: 1.65 }}>
                                · <b>수강권 하나로 2명이 공유하지 않습니다.</b> 2:1 수업이어도 <b>회원 각자에게 수강권 1개씩</b> 발급하는 구조예요.
                                <br />
                                · 2:1 수업을 예약/진행하면 <b>2명 각각의 수강권에서 1회씩</b> 차감됩니다.
                                <br />
                                · 기존 1:1 PT 수강권과 새 그룹 수강권은 <b>서로 독립</b>이라 같이 가지고 있어도 문제 없어요.
                            </span>
                        </Col>
                    </Col>
                </Col>
            )}

            {/* 서브탭별 한 줄 힌트 */}
            {pageType === 'list' && ticketTab === 'group' && (
                <Row style={{ width: 1192, marginTop: 12 }}>
                    <span style={{ fontSize: 13, color: '#6B7280' }}>
                        {groupSubTab === 'tickets' && '판매할 수강권 상품을 등록합니다. 회원에게 발급하거나 수업에 연결해 사용해요.'}
                        {groupSubTab === 'classes' && '수업 템플릿을 만들고 허용 수강권을 연결합니다.'}
                        {groupSubTab === 'settings' && '예약 오픈 · 마감 · 취소 · 대기 등 공통 예약 정책을 설정합니다.'}
                    </span>
                </Row>
            )}

            {/* ★ 서브탭: 수강권 상품 목록 */}
            {pageType === 'list' && ticketTab === 'group' && groupSubTab === 'tickets' && (
                <Col style={{ width: 1192, marginTop: 16 }}>
                    {/* 활성/비활성/전체 필터 */}
                    <Row style={{ gap: 6, marginBottom: 16, paddingLeft: 24 }}>
                        {[
                            { key: 'active' as const, label: '활성', count: activeCount },
                            { key: 'inactive' as const, label: '비활성', count: inactiveCount },
                            { key: 'all' as const, label: '전체', count: (groupTickets as Ticket[]).length },
                        ].map(({ key, label, count }) => {
                            const sel = ticketStatusFilter === key;
                            return (
                                <button key={key}
                                    onClick={() => setTicketStatusFilter(key)}
                                    style={{
                                        padding: '6px 14px', borderRadius: 20, fontSize: 13,
                                        fontWeight: sel ? 600 : 400,
                                        border: sel ? '1.5px solid #3B82F6' : '1px solid #E1E4E6',
                                        backgroundColor: sel ? '#EFF6FF' : 'white',
                                        color: sel ? '#1E40AF' : '#6B7280',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                    }}>
                                    {label}
                                    <span style={{
                                        padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                        backgroundColor: sel ? '#BFDBFE' : '#F3F4F6',
                                        color: sel ? '#1E3A8A' : '#6B7280',
                                    }}>{count}</span>
                                </button>
                            );
                        })}
                    </Row>

                    <div style={{ paddingLeft: 24, display: 'flex', flexWrap: 'wrap' }}>
                        {displayGroupTickets.length === 0 ? (
                            <Col style={{ width: '100%', alignItems: 'center', padding: '80px 0' }}>
                                <span style={{ fontSize: 14, color: '#9CA3AF' }}>
                                    {(groupTickets as Ticket[]).length === 0
                                        ? '수업 수강권이 없습니다. 새로 등록해주세요.'
                                        : `${ticketStatusFilter === 'active' ? '활성' : '비활성'} 상태의 수강권이 없습니다.`}
                                </span>
                            </Col>
                        ) : (
                            displayGroupTickets.map((ticket: Ticket) => (
                                <GroupTicketCard
                                    key={ticket._id}
                                    ticket={ticket}
                                    isSelected={selectedGroupTicket?._id === ticket._id}
                                    onClick={() => setSelectedGroupTicket(prev => prev?._id === ticket._id ? null : ticket)}
                                />
                            ))
                        )}
                    </div>
                </Col>
            )}

            {/* ★ 서브탭: 수업 관리 (임베드) */}
            {pageType === 'list' && ticketTab === 'group' && groupSubTab === 'classes' && (
                <div style={{ width: 1192, marginTop: 24 }}>
                    <GroupClassManagePage createAlert={createAlert} embedded />
                </div>
            )}

            {/* ★ 서브탭: 예약 설정 (임베드) */}
            {pageType === 'list' && ticketTab === 'group' && groupSubTab === 'settings' && (
                <div style={{ width: 1192, marginTop: 24 }}>
                    <GroupSettingsPage
                        createAlert={createAlert}
                        embedded
                        onNavigateToTickets={() => { setGroupSubTab('tickets'); setSelectedGroupTicket(null); }}
                    />
                </div>
            )}

            {/* 기존 PT 수강권 목록 */}
            {pageType === 'list' && ticketTab === 'pt' && (
                <ClassPassList
                    firstSortType={firstSortType}
                    setFirstSortType={setFirstSortType}
                    secondSortType={secondSortType}
                    setSecondSortType={setSecondSortType}
                    filterMembershipTemplates={filterMembershipTemplates}
                    onClickDetailPage={onClickDetailPage}
                    isHideCoach={isHideCoach}
                    setIsHideCoach={setIsHideCoach}
                    onClickHideCoach={() => {
                        setIsHideCoach(!isHideCoach);
                        localStorage.setItem('isHideCoach', JSON.stringify(!isHideCoach));
                    }}
                    onClickReorder={() => {
                        setReorderNotice(null);
                        setIsReorderPassModal(true);
                    }}
                    // 목록을 다시 불러오는 중에는 열지 않는다. 모달은 열 때 스냅샷을 잡아서, 갱신 전 목록을 잡으면
                    // 저장 시 404 가 난다(404 뒤 바로 다시 여는 경우).
                    reorderDisabled={
                        !!authObject?.testAccount ||
                        getMembershipTemplatesList.isFetching ||
                        (membershipTemplates?.length ?? 0) < 2
                    }
                    reorderNotice={reorderNotice}
                    onDismissReorderNotice={() => setReorderNotice(null)}
                />
            )}

            {/* 수강권 표시 순서 변경 — 필터와 무관하게 서버 목록 전체를 서버 순서대로 보여준다 */}
            {isReorderPassModal && (
                <ModalReorderPass
                    isOpen={isReorderPassModal}
                    onClose={() => setIsReorderPassModal(false)}
                    templates={membershipTemplates ?? []}
                    onApply={onApplyReorder}
                    isSaving={reorderMutation.isLoading}
                />
            )}

            {/* ★ 수업 수강권 등록 모달 */}
            {isGroupTicketModal && (
                <ModalRegisterGroupTicket
                    centerId={centerId}
                    onClose={() => setIsGroupTicketModal(false)}
                    createAlert={createAlert}
                />
            )}

            {/* ★ 수업 수강권 상세 — key로 티켓 변경 시 강제 remount (state 초기화) */}
            {selectedGroupTicket && ticketTab === 'group' && groupSubTab === 'tickets' && (
                <GroupTicketDetail
                    key={selectedGroupTicket._id}
                    ticket={selectedGroupTicket}
                    onBack={() => setSelectedGroupTicket(null)}
                    createAlert={createAlert}
                />
            )}

            {pageType === 'detail' && detailObject && (
                <ClassPassDetail
                    authObject={authObject}
                    detailObject={detailObject}
                    onClickModifyPass={onClickModifyPass}
                    createAlert={createAlert}
                    onClickListPage={onClickListPage}
                />
            )}

            {/* 플로팅 버튼 — 수강권 상품 서브탭에서만 노출 */}
            {pageType === 'list' && (
                (() => {
                    if (ticketTab === 'group' && groupSubTab !== 'tickets') return null;
                    return (
                        <FloatingButton
                            onClick={() => {
                                if (ticketTab === 'group') setIsGroupTicketModal(true);
                                else setIsRegisterPassModal(true);
                            }}
                        />
                    );
                })()
            )}

            {isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                />
            )}

            {/* 수강권 수정하기 */}
            {isModifyPassModal && detailObject && (
                <ModalModifyPass
                    setModalVisible={setIsModifyPassModal}
                    createAlert={createAlert}
                    detailObject={detailObject}
                    onUpdateSuccess={(data) => {
                        setDetailObject(data.membershipTemplate);
                    }}
                />
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

const GuideStep = ({ num, title, desc }: { num: string; title: string; desc: string }) => (
    <Col style={{
        flex: 1, minWidth: 200, padding: '10px 14px',
        backgroundColor: 'white', borderRadius: 10, border: '1px solid #BFDBFE', gap: 4,
    }}>
        <Row style={{ alignItems: 'center', gap: 8 }}>
            <span style={{
                width: 20, height: 20, borderRadius: '50%',
                backgroundColor: '#3B82F6', color: 'white',
                fontSize: 11, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>{num}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1E3A8A' }}>{title}</span>
        </Row>
        <span style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.45 }}>{desc}</span>
    </Col>
);

const GuideArrow = () => (
    <div style={{
        display: 'flex', alignItems: 'center', color: '#93C5FD',
        fontSize: 16, fontWeight: 700,
    }}>→</div>
);

export default ClassPassPage;
